import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Button, Paper } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

/**
 * CustomerHeader - Displays customer information and back navigation
 * 
 * Shows customer name and key information with a back button
 * 
 * Requirements: 2.2, 2.5
 * 
 * @param {Object} props
 * @param {Object} props.customer - The customer object
 * @param {string} props.customer.customerName - The customer's name
 * @param {string} props.customer.customerCode - The customer's code
 * @param {string} props.customer.organizationDetails - Organization details
 * @param {boolean} props.customer.isActive - Whether the customer is active
 * @param {Function} props.onBack - Callback when back button is clicked
 */
const CustomerHeader = ({ customer, onBack }) => {
  if (!customer) {
    return null;
  }

  return (
    <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          variant="outlined"
          size="small"
          sx={{ mr: 2 }}
          aria-label="Back to customers"
        >
          Back to Customers
        </Button>
      </Box>
      
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          {customer.customerName}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Customer Code
            </Typography>
            <Typography variant="body1">
              {customer.customerCode}
            </Typography>
          </Box>
          
          {customer.organizationDetails && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Organization
              </Typography>
              <Typography variant="body1">
                {customer.organizationDetails}
              </Typography>
            </Box>
          )}
          
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Status
            </Typography>
            <Typography 
              variant="body1" 
              color={customer.isActive ? 'success.main' : 'error.main'}
            >
              {customer.isActive ? 'Active' : 'Inactive'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

CustomerHeader.propTypes = {
  customer: PropTypes.shape({
    customerName: PropTypes.string.isRequired,
    customerCode: PropTypes.string.isRequired,
    organizationDetails: PropTypes.string,
    isActive: PropTypes.bool.isRequired
  }),
  onBack: PropTypes.func.isRequired
};

export default CustomerHeader;
